import type { MembershipFormatters } from './format';
import { type MembershipLine, planTermLines } from './lines';
import { periodEnd, planChangeDirection } from './manage';
import { type MembershipWelcome, welcomeLineOf } from './purchase';
import { isTrialEligible, trialKey } from './trial';
import type { Membership, MembershipPlan } from './types';
import type { MembershipView } from './view';

const thresholdOf = (plan: MembershipPlan): number =>
  typeof plan.freeDeliveryThresholdMinor === 'number'
    ? plan.freeDeliveryThresholdMinor
    : Number.POSITIVE_INFINITY;

/**
 * The plan the club leads with: the lowest member free-delivery threshold of
 * the zone, the cheaper one on a tie; `null` for an empty list.
 */
export const bestClubPlan = (
  plans: readonly MembershipPlan[],
): MembershipPlan | null =>
  plans.reduce<MembershipPlan | null>((best, plan) => {
    if (!best) {
      return plan;
    }
    const planThreshold = thresholdOf(plan);
    const bestThreshold = thresholdOf(best);
    if (planThreshold !== bestThreshold) {
      return planThreshold < bestThreshold ? plan : best;
    }
    return plan.priceMinor < best.priceMinor ? plan : best;
  }, null);

/** What the club offers a non-member in the current zone; `null` for a member, a guest, an error or while loading. */
export const clubOffer = (view: MembershipView): MembershipPlan | null =>
  view.kind === 'offer' ? bestClubPlan(view.plans) : null;

/**
 * What the club saved the customer so far, in minor units; `0` without a
 * membership, without savings or when the backend could not compute them.
 */
export const clubSavedMinor = (membership: Membership | null): number =>
  Math.max(membership?.savings?.totalMinor ?? 0, 0);

/**
 * Whether the club entry points (home banner, profile card) show for `view`:
 * `true` for an offer whose plan advertises a threshold, `false` once the
 * view resolved without one (a member, no address or plans, an error),
 * `null` while it loads.
 */
export const clubEntryShown = (view: MembershipView): boolean | null => {
  if (view.kind === 'loading') {
    return null;
  }
  const plan = clubOffer(view);
  return plan !== null && clubEntryThresholdMinor(plan) !== null;
};

/**
 * Whether an entry point holds its space while the offer resolves: only when
 * it is likely to show — it showed last time, or nothing is known yet — so a
 * member or a zone without plans never sees a placeholder collapse.
 */
export const reservesClubEntry = (
  isPending: boolean,
  lastShown: boolean | null,
): boolean => isPending && lastShown !== false;

/** The plan Join opens with: the one a link asked for when the zone offers it, the best one otherwise. */
export const joinPlan = (
  plans: readonly MembershipPlan[],
  requestedId: string | null,
): MembershipPlan | null =>
  plans.find((plan) => plan.id === requestedId) ?? bestClubPlan(plans);

/**
 * The regular free-delivery threshold shown by the club copy ("Without
 * CityDrinks Club, free delivery starts at …", "Everyone else pays until …"),
 * in minor units: AED 200. Hardcoded by product decision instead of being read
 * from the ETA or the cart.
 */
export const CLUB_PUBLIC_THRESHOLD_MINOR = 20000;

/**
 * The big translucent figure of a hero: the threshold in whole units, rounded
 * up so it never promises less than the real threshold; `null` without a
 * positive threshold.
 */
export const thresholdFigure = (
  thresholdMinor: number | null,
): string | null =>
  thresholdMinor !== null && thresholdMinor > 0
    ? String(Math.ceil(thresholdMinor / 100))
    : null;

/**
 * Threshold a club entry point (home banner, profile card, Join promo)
 * advertises: the member threshold, `0` for free delivery on every order
 * (the entry points then use their "Always" copy), `null` for a plan without
 * a threshold, which gets no entry point.
 */
export const clubEntryThresholdMinor = (
  plan: Pick<MembershipPlan, 'freeDeliveryThresholdMinor'>,
): number | null => {
  const threshold = plan.freeDeliveryThresholdMinor;
  return typeof threshold === 'number' && threshold >= 0 ? threshold : null;
};

/** Length of the price copy: "/month", or "/mo" under the mobile home banner. */
export type ClubPriceLength = 'long' | 'short';

/** "First month free" of a plan with a trial for this customer; `null` otherwise. */
export const clubTrialLine = (plan: MembershipPlan): MembershipLine | null =>
  isTrialEligible(plan)
    ? {
        key: trialKey('membership:trial', plan.trialUnit),
        params: { count: plan.trialLength },
      }
    : null;

/**
 * "{price}/month after your free month" ("{price}/mo after" when short) of a
 * plan with a trial for this customer, "{price}/month" ("/mo") otherwise.
 */
export const clubPriceLine = (
  plan: MembershipPlan,
  f: MembershipFormatters,
  length: ClubPriceLength = 'long',
): MembershipLine => {
  const price = f.money(plan.priceMinor, plan.currency);
  const suffix = length === 'short' ? 'Short' : '';
  return isTrialEligible(plan)
    ? {
        key: `${trialKey('membership:clubPriceAfterTrial', plan.trialUnit)}${suffix}`,
        params: { count: plan.trialLength, price },
      }
    : { key: `membership:clubPrice${suffix}`, params: { price } };
};

/** The main club button: "Start my free month" for a plan with a trial for this customer, "Join CityDrinks Club" otherwise. */
export const clubJoinCtaLine = (plan: MembershipPlan): MembershipLine =>
  isTrialEligible(plan)
    ? {
        key: trialKey('membership:clubStartTrial', plan.trialUnit),
        params: { count: plan.trialLength },
      }
    : { key: 'membership:clubJoinCta' };

/** Title of Join: "Join the CityDrinks Club, first month is free" with a trial for this customer. */
export const clubJoinTitleLine = (plan: MembershipPlan): MembershipLine =>
  isTrialEligible(plan)
    ? {
        key: trialKey('membership:clubJoinTitleTrial', plan.trialUnit),
        params: { count: plan.trialLength },
      }
    : { key: 'membership:clubJoinTitle' };

/** Subtitle of Join: "After free month {price}/month" with a trial for this customer, "{price}/month" otherwise. */
export const clubJoinSubtitleLine = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine => {
  const price = f.money(plan.priceMinor, plan.currency);
  return isTrialEligible(plan)
    ? {
        key: trialKey('membership:clubJoinSubtitleTrial', plan.trialUnit),
        params: { count: plan.trialLength, price },
      }
    : { key: 'membership:clubPrice', params: { price } };
};

const byThreshold = (
  thresholdMinor: number | null,
  currency: string,
  f: MembershipFormatters,
  keys: { from: string; always: string; generic: string },
): MembershipLine => {
  if (thresholdMinor === null) {
    return { key: keys.generic };
  }
  if (thresholdMinor === 0) {
    return { key: keys.always };
  }
  return {
    key: keys.from,
    params: { threshold: f.money(thresholdMinor, currency) },
  };
};

/** Hero title of a non-member: "Free delivery on every order {threshold}+". */
export const clubHeroTitleLine = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine =>
  byThreshold(plan.freeDeliveryThresholdMinor, plan.currency, f, {
    from: 'membership:clubHeroTitle',
    always: 'membership:clubHeroTitleAlways',
    generic: 'membership:clubHeroTitleGeneric',
  });

/** "Without CityDrinks Club, free delivery starts at {public}." */
export const clubHeroPublicLine = (
  currency: string,
  f: MembershipFormatters,
): MembershipLine => ({
  key: 'membership:clubHeroPublic',
  params: { threshold: f.money(CLUB_PUBLIC_THRESHOLD_MINOR, currency) },
});

/** One benefit card of the club page. */
export interface ClubBenefitCard {
  id: 'trial' | 'unlimited' | 'threshold';
  title: MembershipLine;
  text: MembershipLine;
}

/** The benefit cards of the club page; the trial card only for a plan with a trial for this customer. */
export const clubBenefitCards = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): ClubBenefitCard[] => {
  const cards: ClubBenefitCard[] = [];
  const trial = clubTrialLine(plan);
  const price = f.money(plan.priceMinor, plan.currency);
  if (trial) {
    cards.push({
      id: 'trial',
      title: trial,
      text: {
        key: trialKey('membership:clubBenefitTrialText', plan.trialUnit),
        params: {
          count: plan.trialLength,
          price,
          zero: f.money(0, plan.currency),
        },
      },
    });
  }
  cards.push({
    id: 'unlimited',
    title: { key: 'membership:clubBenefitUnlimitedTitle' },
    text: { key: 'membership:clubBenefitUnlimitedText' },
  });
  const threshold = plan.freeDeliveryThresholdMinor;
  if (threshold === null) {
    return cards;
  }
  const text: MembershipLine =
    threshold === 0
      ? { key: 'membership:clubBenefitThresholdTextAlways' }
      : {
          key: 'membership:clubBenefitThresholdText',
          params: {
            threshold: f.money(threshold, plan.currency),
            public: f.money(CLUB_PUBLIC_THRESHOLD_MINOR, plan.currency),
          },
        };
  cards.push({
    id: 'threshold',
    title: { key: 'membership:clubBenefitThresholdTitle' },
    text,
  });
  return cards;
};

/** One benefit card of the club modals. */
export interface ClubBenefit {
  /** Picks the icon: the rider for delivery, the badge for the discount. */
  kind: 'delivery' | 'discount';
  title: MembershipLine;
  /** Second, muted line; `null` when the title says it all. */
  note: MembershipLine | null;
}

/**
 * Benefit cards of the club modals, at most two: free delivery from the
 * threshold, and the discount. With several discount steps the card shows the
 * top percent and the range from the first step to the last; the steps in
 * between are left out.
 */
export const clubBenefits = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): ClubBenefit[] => {
  const benefits: ClubBenefit[] = [];
  const delivery = planTermLines(plan, f).find((line) =>
    line.key.startsWith('membership:freeDelivery'),
  );
  if (delivery) {
    benefits.push({ kind: 'delivery', title: delivery, note: null });
  }

  const tiers = (plan.discountTiers ?? [])
    .filter((tier) => tier.percent > 0)
    .sort((a, b) => a.floorMinor - b.floorMinor);
  const first = tiers[0];
  const last = tiers[tiers.length - 1];
  if (!first || !last) {
    return benefits;
  }

  const money = (minor: number) => f.money(minor, plan.currency);
  if (tiers.length === 1) {
    benefits.push({
      kind: 'discount',
      title:
        first.floorMinor > 0
          ? {
              key: 'membership:tierFrom',
              params: {
                percent: first.percent,
                floor: money(first.floorMinor),
              },
            }
          : {
              key: 'membership:tierEveryOrder',
              params: { percent: first.percent },
            },
      note: null,
    });
    return benefits;
  }

  benefits.push({
    kind: 'discount',
    title: {
      key: 'membership:clubTierUpTo',
      params: { percent: last.percent },
    },
    note:
      first.floorMinor > 0
        ? {
            key: 'membership:clubTierRange',
            params: {
              firstPercent: first.percent,
              firstFloor: money(first.floorMinor),
              lastPercent: last.percent,
              lastFloor: money(last.floorMinor),
            },
          }
        : {
            key: 'membership:clubTierRangeAnyOrder',
            params: {
              firstPercent: first.percent,
              lastPercent: last.percent,
              lastFloor: money(last.floorMinor),
            },
          },
  });
  return benefits;
};

/** Subtitle of Welcome, stated by the purchase answer: trial end, today's charge, or neither. */
export const clubWelcomeLine = (
  welcome: MembershipWelcome,
  f: MembershipFormatters,
): MembershipLine =>
  welcomeLineOf(welcome, f, {
    charged: 'membership:clubWelcomeCharged',
    trial: 'membership:clubWelcomeTrial',
    generic: 'membership:clubWelcome',
  });

/** Hero title of a member: "Your delivery is free from {threshold}"; benefits paused while `past_due`, so no threshold is promised. */
export const memberHeroTitleLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine =>
  membership.status === 'past_due'
    ? { key: 'membership:clubMemberHeroTitlePaused' }
    : byThreshold(
        membership.plan.freeDeliveryThresholdMinor,
        membership.plan.currency,
        f,
        {
          from: 'membership:clubMemberHeroTitle',
          always: 'membership:clubMemberHeroTitleAlways',
          generic: 'membership:clubMemberHeroTitleGeneric',
        },
      );

/** "Everyone else pays until {public}"; `null` while `past_due`, when the member pays like everyone else. */
export const memberHeroPublicLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine | null =>
  membership.status === 'past_due'
    ? null
    : {
        key: 'membership:clubMemberHeroPublic',
        params: {
          threshold: f.money(
            CLUB_PUBLIC_THRESHOLD_MINOR,
            membership.plan.currency,
          ),
        },
      };

/** Threshold of the member hero figure; `null` (no figure) while `past_due`. */
export const memberHeroThresholdMinor = (
  membership: Membership,
): number | null =>
  membership.status === 'past_due'
    ? null
    : membership.plan.freeDeliveryThresholdMinor;

/** Text of the cancel card at the bottom of the club page; `null` when there is no date to promise. */
export const clubCancelCardLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine | null => {
  const date =
    membership.status === 'past_due' ? null : f.date(periodEnd(membership));
  if (!date) {
    return null;
  }
  return {
    key: 'membership:clubCancelCardText',
    params: {
      date,
      threshold: f.money(CLUB_PUBLIC_THRESHOLD_MINOR, membership.plan.currency),
    },
  };
};

/** Cancel confirmation of a `past_due` membership: no retry is promised after a hard decline. */
const pastDueCancelLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine => {
  const issue = membership.paymentIssue ?? null;
  if (issue?.cardUnusable) {
    return { key: 'membership:cancelTextPastDueCardUnusable' };
  }
  const retryOn = issue ? f.date(issue.nextAttemptAt) : null;
  return retryOn
    ? {
        key: 'membership:cancelTextPastDueRetry',
        params: { retryDate: retryOn },
      }
    : { key: 'membership:cancelTextPastDue' };
};

/**
 * Text of the cancel confirmation: the member threshold kept until the period
 * end and the public one after it when the plan has a threshold; the plain
 * copy of part C otherwise (paused benefits while `past_due`, no date when
 * there is none).
 */
export const clubCancelSheetLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine => {
  if (membership.status === 'past_due') {
    return pastDueCancelLine(membership, f);
  }
  const date = f.date(periodEnd(membership));
  if (!date) {
    return { key: 'membership:cancelTextNoDate' };
  }
  const threshold = membership.plan.freeDeliveryThresholdMinor;
  if (threshold === null || threshold <= 0) {
    return { key: 'membership:cancelText', params: { date } };
  }
  return {
    key: 'membership:clubCancelText',
    params: {
      date,
      threshold: f.money(threshold, membership.plan.currency),
      public: f.money(CLUB_PUBLIC_THRESHOLD_MINOR, membership.plan.currency),
    },
  };
};

/** One plan of the zone the member can switch to, as the plans card shows it. */
export interface ClubPlanRow {
  plan: MembershipPlan;
  direction: 'upgrade' | 'downgrade';
  title: MembershipLine;
  /** Under the title of an upgrade; `null` for a downgrade. */
  note: MembershipLine | null;
  /** Button of a downgrade; `null` for an upgrade, which is a whole clickable row. */
  action: MembershipLine | null;
}

/**
 * Rows of the plans card. An upgrade ("Add {plan} for the rest of this
 * period") applies at once and never names the amount, which only the backend
 * knows; a downgrade ("Switch from {date}") applies at the period end. The
 * member's own plan is never a row, whatever list is passed in.
 */
export const clubPlanRows = (
  membership: Membership,
  plans: readonly MembershipPlan[],
  f: MembershipFormatters,
): ClubPlanRow[] =>
  plans
    .filter((plan) => plan.id !== membership.plan.id)
    .map((plan): ClubPlanRow => {
      const direction = planChangeDirection(membership, plan);
      if (direction === 'upgrade') {
        return {
          plan,
          direction,
          title: {
            key: 'membership:clubPlanUpgrade',
            params: { planName: plan.name },
          },
          note: {
            key:
              membership.status === 'trialing'
                ? 'membership:clubPlanUpgradeTrialNote'
                : 'membership:clubPlanUpgradeNote',
          },
          action: null,
        };
      }
      const price = f.money(plan.priceMinor, plan.currency);
      const date = f.date(periodEnd(membership));
      return {
        plan,
        direction,
        title: date
          ? {
              key: 'membership:clubPlanDowngrade',
              params: { planName: plan.name, price, date },
            }
          : {
              key: 'membership:clubPlanDowngradeNoDate',
              params: { planName: plan.name, price },
            },
        note: null,
        action: date
          ? { key: 'membership:clubPlanSwitchFrom', params: { date } }
          : { key: 'membership:switchPlan' },
      };
    });

/** Picture of a plan's zone in the upgrade modal; only Dubai has one so far. */
export type ClubZoneImage = 'dubai';

/**
 * The only place that ties plans to zone pictures: plan code → picture. The
 * backend has no zone field on a plan, so a plan gets a picture only when its
 * code is listed here. Known Dubai plans so far: `e2e-test-plan` on stage; the
 * production codes are added once the backend creates them.
 */
export const CLUB_ZONE_IMAGE_BY_PLAN_CODE: ReadonlyMap<string, ClubZoneImage> =
  new Map<string, ClubZoneImage>([['e2e-test-plan', 'dubai']]);

/** The zone picture of a plan by its code; `null` for a code that is not listed. */
export const planZoneImage = (
  plan: Pick<MembershipPlan, 'code'>,
): ClubZoneImage | null => CLUB_ZONE_IMAGE_BY_PLAN_CODE.get(plan.code) ?? null;

/** One row of the plan terms: what qualifies, and what it gets. */
export interface ClubTermRow {
  id: string;
  label: MembershipLine;
  value: MembershipLine;
}

/** The full terms of a plan for the club page: free delivery and every discount step. */
export interface ClubPlanTerms {
  /** Free-delivery row; empty when the plan states no threshold. */
  delivery: ClubTermRow[];
  /** Every discount step, lowest order first. */
  discount: ClubTermRow[];
  /** Whether the discount covers reduced items; `null` without discount steps. */
  markdown: MembershipLine | null;
}

const orderFromLine = (
  floorMinor: number,
  plan: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine =>
  floorMinor > 0
    ? {
        key: 'membership:clubTermsOrdersFrom',
        params: { floor: f.money(floorMinor, plan.currency) },
      }
    : { key: 'membership:clubTermsAnyOrder' };

/**
 * Detailed terms of a plan for the club page — the Join modal shows only the
 * first and the last discount step, this lists them all.
 */
export const clubPlanTerms = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): ClubPlanTerms => {
  const threshold = plan.freeDeliveryThresholdMinor;
  const delivery: ClubTermRow[] =
    typeof threshold === 'number'
      ? [
          {
            id: 'delivery',
            label: orderFromLine(threshold, plan, f),
            value: { key: 'membership:clubTermsFree' },
          },
        ]
      : [];
  const discount = (plan.discountTiers ?? [])
    .filter((tier) => tier.percent > 0)
    .sort((a, b) => a.floorMinor - b.floorMinor)
    .map((tier) => ({
      id: `tier-${tier.floorMinor}`,
      label: orderFromLine(tier.floorMinor, plan, f),
      value: {
        key: 'membership:clubTermsPercent',
        params: { percent: tier.percent },
      },
    }));
  return {
    delivery,
    discount,
    markdown:
      discount.length > 0
        ? {
            key: plan.appliesToMarkdown
              ? 'membership:markdownIncluded'
              : 'membership:markdownExcluded',
          }
        : null,
  };
};
